import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ErrorPage from '@/components/ErrorPage';

/**
 * Route-level error element for React Router (data router).
 *
 * Without this, render/runtime errors inside any route are caught by React
 * Router's built-in default errorElement, which prints the raw error message
 * and stack trace to end users — flagged as "Unnecessary information
 * disclosure" in the May 2026 VAPT (finding #18).
 *
 * React Router docs recommend always providing a root-level errorElement
 * before shipping to production. Errors bubble up to the nearest
 * errorElement, so attaching this on the root route covers the entire tree.
 */
export default function RouteErrorElement() {
  const error = useRouteError();

  useEffect(() => {
    // Full details stay available to developers (console) and the team (Sentry),
    // but are never rendered in the UI.
    console.error('Route error caught by RouteErrorElement:', error);

    Sentry.withScope((scope) => {
      scope.setTag('route_error_element', true);
      scope.setLevel('fatal');
      Sentry.captureException(error);
    });
  }, [error]);

  return (
    <ErrorPage
      title="Oops!"
      subtitle="Something went wrong"
      description="An unexpected error occurred while loading this page. The issue has been logged and our team will look into it. Please try again or head back to the dashboard."
      primaryButtonText="Dashboard"
      secondaryButtonText="Go back"
      primaryButtonLink="/"
    />
  );
}
